"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import Section, { Reveal } from "../layout/Section";
import {
  LEVEL_COPY,
  SKILL_GROUPS,
  type Level,
  type Skill,
} from "../../data/skills";

const LEVELS: Level[] = ["core", "working", "familiar"];

export default function Skills() {
  const [filter, setFilter] = React.useState<Level | null>(null);

  return (
    <Section
      id="skills"
      index="03"
      title="Skills"
      lede="Banded by how deep it goes, and labelled with where it was used. Hover or tap anything to see the receipt."
    >
      <Reveal>
        <div className="flex flex-wrap items-center gap-3">
          <div className="seg" role="group" aria-label="Filter by depth">
            <button
              type="button"
              className="seg-item"
              aria-pressed={filter === null}
              onClick={() => setFilter(null)}
            >
              All
            </button>
            {LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                className="seg-item"
                aria-pressed={filter === l}
                onClick={() => setFilter(filter === l ? null : l)}
              >
                {LEVEL_COPY[l].label}
              </button>
            ))}
          </div>
          <p className="text-2xs text-faint">
            {filter
              ? LEVEL_COPY[filter].hint
              : "Core · daily driver. Working · shipped real work with it. Familiar · used it, would want a ramp."}
          </p>
        </div>
      </Reveal>

      <div className="mt-10 grid gap-px overflow-hidden rounded-xl border bg-[rgb(var(--line)/var(--line-a))] md:grid-cols-2">
        {SKILL_GROUPS.map((group, i) => {
          const visible = filter
            ? group.skills.filter((s) => s.level === filter)
            : group.skills;

          return (
            <Reveal key={group.title} delay={i * 40}>
              <div className="flex h-full flex-col bg-bg p-5 md:p-6">
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="text-base font-medium">{group.title}</h3>
                  <span className="font-mono text-2xs text-faint">
                    {visible.length}
                  </span>
                </div>
                <p className="mt-1 text-2xs text-faint">{group.note}</p>

                {visible.length === 0 ? (
                  <p className="mt-5 text-sm text-faint">
                    Nothing in this band.
                  </p>
                ) : (
                  <ul className="mt-5 flex flex-wrap gap-1.5">
                    {visible.map((s) => (
                      <SkillPill key={s.name} skill={s} />
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
}

const LEVEL_STYLE: Record<Level, string> = {
  core: "border-accent/45 bg-accent/[0.09] text-ink",
  working: "border-[rgb(var(--line)/0.2)] bg-raised text-ink",
  familiar:
    "border-dashed border-[rgb(var(--line)/0.22)] bg-transparent text-faint",
};

function SkillPill({ skill }: { skill: Skill }) {
  const [open, setOpen] = React.useState(false);
  const pillRef = React.useRef<HTMLSpanElement | null>(null);
  const hasWhere = Boolean(skill.where);
  const id = `${skill.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-where`;

  // Anything that moves the pill closes the tip. It is positioned in viewport
  // space, so it would otherwise drift away from its anchor.
  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true });
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <li>
      <span
        ref={pillRef}
        className={[
          "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[0.8125rem] transition-colors",
          LEVEL_STYLE[skill.level],
          hasWhere ? "cursor-help" : "",
        ].join(" ")}
        tabIndex={hasWhere ? 0 : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // Touch has no hover, so a tap toggles instead. On a pointer device
        // the hover handlers already own the state and a click is a no-op.
        onClick={() => {
          if (window.matchMedia?.("(hover: none)").matches) setOpen((v) => !v);
        }}
        aria-describedby={hasWhere && open ? id : undefined}
      >
        {skill.level === "core" && (
          <span className="h-1 w-1 rounded-full bg-accent" aria-hidden />
        )}
        {skill.name}
      </span>

      {hasWhere && open && (
        <Tip id={id} anchor={pillRef}>
          {skill.where}
        </Tip>
      )}
    </li>
  );
}

/**
 * The receipt, rendered into <body> in viewport coordinates.
 *
 * The pill grid clips its overflow to keep its rounded corners, so anything
 * positioned inside it gets cut at the grid edge, and a pill on the right-hand
 * side of the last column would lose most of its tip. Portalling out and
 * clamping to the viewport fixes both at once.
 */
function Tip({
  id,
  anchor,
  children,
}: React.PropsWithChildren<{
  id: string;
  anchor: React.RefObject<HTMLElement | null>;
}>) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(
    null,
  );

  React.useLayoutEffect(() => {
    const a = anchor.current;
    const t = ref.current;
    if (!a || !t) return;
    const r = a.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const w = t.offsetWidth;
    const gutter = 8;
    setPos({
      top: r.top - 6,
      left: Math.max(gutter, Math.min(r.left, vw - w - gutter)),
    });
  }, [anchor]);

  return createPortal(
    <span
      ref={ref}
      id={id}
      role="tooltip"
      className="pointer-events-none fixed z-[70] w-max max-w-[16rem] -translate-y-full rounded-md border bg-surface px-2.5 py-1.5 text-2xs leading-snug text-muted shadow-lg"
      style={
        pos
          ? { top: pos.top, left: pos.left }
          : { top: 0, left: 0, visibility: "hidden" }
      }
    >
      {children}
    </span>,
    document.body,
  );
}
